// Scenario registry for DEFCON TERMINAL frontend

import type { PathType, Scenario } from '@shared/types';

// Vite imports JSON files directly with eager loading
const scenarioModules = import.meta.glob('@scenarios/**/*.json', {
  eager: true,
}) as Record<string, { default?: Scenario } | Scenario>;

// Handle both { default: Scenario } and direct Scenario formats
const scenarios: Scenario[] = Object.entries(scenarioModules)
  .map(([path, mod]) => {
    // Check if module has default export or is the scenario directly
    const scenario = (mod as { default?: Scenario }).default || (mod as Scenario);
    if (!scenario?.id) {
      console.warn(`Scenario at ${path} has no id:`, scenario);
    }
    return scenario;
  })
  .filter((scenario): scenario is Scenario => !!scenario?.id);

// Log scenario count (helpful for debugging production issues)
console.log(`[DEFCON] Loaded ${scenarios.length} scenarios`);
if (scenarios.length === 0) {
  console.error('[DEFCON] No scenarios loaded! Check import.meta.glob configuration.');
  console.log('[DEFCON] scenarioModules:', Object.keys(scenarioModules));
}

const scenarioById = new Map<string, Scenario>();
const scenarioByPath = new Map<PathType, Scenario[]>();

for (const scenario of scenarios) {
  scenarioById.set(scenario.id, scenario);

  const list = scenarioByPath.get(scenario.path) ?? [];
  list.push(scenario);
  scenarioByPath.set(scenario.path, list);
}

for (const [path, list] of scenarioByPath.entries()) {
  list.sort((a, b) => a.title.localeCompare(b.title));
  scenarioByPath.set(path, list);
}

export function getScenarioById(id: string): Scenario | null {
  return scenarioById.get(id) ?? null;
}

export function getScenariosByPath(path: PathType): Scenario[] {
  return scenarioByPath.get(path) ?? [];
}

export function getScenarioSummaries(path: PathType): Array<{
  id: string;
  title: string;
  description: string;
}> {
  return getScenariosByPath(path).map((scenario) => ({
    id: scenario.id,
    title: scenario.title,
    description: scenario.description,
  }));
}

export function getAllScenarios(): Scenario[] {
  return [...scenarios];
}
